import React, { useState } from 'react';
import { FiSearch, FiDownload, FiRefreshCw, FiTrendingDown, FiDollarSign } from 'react-icons/fi';
import api from '../services/api';

const WebScraping = () => {
  const [activeTab, setActiveTab] = useState('single');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState('');

  // Single scrape form
  const [singleForm, setSingleForm] = useState({
    marketplace: 'amazon',
    product_id: '',
  });

  // Compare prices form
  const [compareForm, setCompareForm] = useState({
    products: [
      { marketplace: 'amazon', product_id: '' },
      { marketplace: 'flipkart', product_id: '' },
    ],
  });

  const handleSingleScrape = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarning('');
    setResult(null);

    try {
      // Try real scraping first
      const response = await api.post('/scraping/scrape-product.php', singleForm);
      
      console.log('Full API Response:', response.data);
      console.log('Data field:', response.data.data);
      console.log('Note field:', response.data.data?.note);
      console.log('Message field:', response.data.message);
      
      const responseData = response.data.data;
      const note = responseData?.note;
      const data = responseData?.data;
      
      // Check if we got actual data
      if (data && (data.title === 'N/A' || !data.title || data.price === 'N/A' || !data.price)) {
        setError('Could not extract data. The website may have blocked the request or changed its structure. Try using a different product ID.');
      } else {
        setResult(data);
        
        // Show warning if using sample data
        if (note) {
          setWarning(note);
        }
      }
    } catch (err) {
      console.error('Scraping error:', err);
      console.error('Error response:', err.response);
      setError(err.response?.data?.message || 'Scraping failed. Website may have anti-scraping protection.');
    } finally {
      setLoading(false);
    }
  };

  const handleComparePrices = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setWarning('');
    setResult(null);

    try {
      const response = await api.post('/scraping/compare-prices.php', compareForm);
      const responseData = response.data.data;
      setResult(responseData);
      
      // Check if any results used sample data
      if (responseData?.note) {
        setWarning(responseData.note);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Price comparison failed');
    } finally {
      setLoading(false);
    }
  };

  const addProduct = () => {
    setCompareForm({
      ...compareForm,
      products: [...compareForm.products, { marketplace: 'amazon', product_id: '' }],
    });
  };

  const removeProduct = (index) => {
    const newProducts = compareForm.products.filter((_, i) => i !== index);
    setCompareForm({ ...compareForm, products: newProducts });
  };

  const updateProduct = (index, field, value) => {
    const newProducts = [...compareForm.products];
    newProducts[index][field] = value;
    setCompareForm({ ...compareForm, products: newProducts });
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-800 dark:text-white">Web Scraping</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Extract product data and compare prices across marketplaces
        </p>
      </div>

      {/* Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md">
        <div className="border-b border-gray-200 dark:border-gray-700">
          <nav className="flex space-x-8 px-6">
            <button
              onClick={() => setActiveTab('single')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'single'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Single Product
            </button>
            <button
              onClick={() => setActiveTab('compare')}
              className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === 'compare'
                  ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Compare Prices
            </button>
          </nav>
        </div>

        <div className="p-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {warning && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-400 px-4 py-3 rounded-lg mb-6">
              ⚠️ {warning}
            </div>
          )}

          {/* Info Box */}
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 text-blue-800 dark:text-blue-300 px-4 py-3 rounded-lg mb-6">
            <p className="text-sm font-semibold mb-1">ℹ️ About Web Scraping</p>
            <ul className="text-xs list-disc list-inside space-y-1">
              <li>Marketplaces like Amazon & Flipkart have anti-scraping protection</li>
              <li>If scraping returns N/A, the website blocked or changed its structure</li>
              <li>For production use, consider using official marketplace APIs</li>
              <li>This feature works best with simple product pages</li>
            </ul>
          </div>

          {activeTab === 'single' && (
            <form onSubmit={handleSingleScrape} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Marketplace
                  </label>
                  <select
                    value={singleForm.marketplace}
                    onChange={(e) => setSingleForm({ ...singleForm, marketplace: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                  >
                    <option value="amazon">Amazon</option>
                    <option value="flipkart">Flipkart</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Product ID / ASIN
                  </label>
                  <input
                    type="text"
                    value={singleForm.product_id}
                    onChange={(e) => setSingleForm({ ...singleForm, product_id: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="e.g., B08N5WRWNW"
                    required
                  />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
              >
                {loading ? <FiRefreshCw className="animate-spin" /> : <FiSearch />}
                {loading ? 'Scraping...' : 'Scrape Product'}
              </button>
            </form>
          )}

          {activeTab === 'compare' && (
            <form onSubmit={handleComparePrices} className="space-y-6">
              <div className="space-y-4">
                {compareForm.products.map((product, index) => (
                  <div key={index} className="flex gap-4 items-end">
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Marketplace
                      </label>
                      <select
                        value={product.marketplace}
                        onChange={(e) => updateProduct(index, 'marketplace', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="amazon">Amazon</option>
                        <option value="flipkart">Flipkart</option>
                      </select>
                    </div>

                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Product ID
                      </label>
                      <input
                        type="text"
                        value={product.product_id}
                        onChange={(e) => updateProduct(index, 'product_id', e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Product ID"
                        required
                      />
                    </div>

                    {compareForm.products.length > 2 && (
                      <button
                        type="button"
                        onClick={() => removeProduct(index)}
                        className="px-4 py-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={addProduct}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  + Add Product
                </button>

                <button
                  type="submit"
                  disabled={loading}
                  className="flex items-center gap-2 px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {loading ? <FiRefreshCw className="animate-spin" /> : <FiDollarSign />}
                  {loading ? 'Comparing...' : 'Compare Prices'}
                </button>
              </div>
            </form>
          )}

          {/* Results */}
          {result && (
            <div className="mt-8 space-y-6">
              <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Results</h3>

              {result.analysis ? (
                // Price comparison results
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Best Price</p>
                      <p className="text-2xl font-bold text-green-600">₹{result.analysis.min_price}</p>
                      <p className="text-xs text-gray-500">{result.analysis.best_marketplace}</p>
                    </div>

                    <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Highest Price</p>
                      <p className="text-2xl font-bold text-red-600">₹{result.analysis.max_price}</p>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Average Price</p>
                      <p className="text-2xl font-bold text-blue-600">₹{result.analysis.avg_price}</p>
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                      <p className="text-sm text-gray-600 dark:text-gray-400">You Save</p>
                      <p className="text-2xl font-bold text-purple-600">{result.analysis.savings_percent}%</p>
                      <p className="text-xs text-gray-500">₹{result.analysis.price_difference}</p>
                    </div>
                  </div>

                  <div className="bg-white dark:bg-gray-700 p-4 rounded-lg">
                    <h4 className="font-semibold mb-2 text-gray-800 dark:text-white">All Prices</h4>
                    <div className="space-y-2">
                      {Object.entries(result.analysis.all_prices).map(([marketplace, price]) => (
                        <div key={marketplace} className="flex justify-between items-center">
                          <span className="text-gray-700 dark:text-gray-300 capitalize">{marketplace}</span>
                          <span className="font-bold text-gray-800 dark:text-white">₹{price}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                // Single product results
                <div className="bg-white dark:bg-gray-700 p-6 rounded-lg space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Title</p>
                      <p className="font-medium text-gray-800 dark:text-white">{result.title || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Price</p>
                      <p className="text-2xl font-bold text-green-600">₹{result.price || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Rating</p>
                      <p className="font-medium text-gray-800 dark:text-white">{result.rating || 'N/A'} ⭐</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Reviews</p>
                      <p className="font-medium text-gray-800 dark:text-white">{result.reviews || '0'}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebScraping;
